import { useGroupedFormsBehaviors } from 'hooks';
import {
  GroupedFormsBehaviorsOnSubmitArgsType,
  GroupedFormsOnSubmitResultType,
} from 'hooks/useGroupedFormsBehaviors/useGroupedFormsBehaviors.types';
import { createShiftsPageInitialState } from 'pages/CreateShiftsPage/CreateShiftsPage.constants';
import {
  CreateShiftsPageViewType,
  ICreateShiftsPageContext,
} from 'pages/CreateShiftsPage/CreateShiftsPage.types';
import {
  createOrdersPayloadTransformer,
  getInitialCreateShiftForm,
  getInitialCreateShiftsPageForm,
  validateAndSetupCustomerValidation,
} from 'pages/CreateShiftsPage/CreateShiftsPage.utils';
import {
  FC,
  MouseEventHandler,
  useCallback,
  useContext,
  useState,
  useEffect,
  useMemo,
} from 'react';
import { API_POST_createOrders } from 'requests/POST_createOrders';
import { toastService } from 'services';
import { withMemo } from 'utils/withMemo';
import { useAPIActions } from 'store/reducers/api/apiSlice';
import { history } from 'utils/history';
import isUserEssentialSelector from 'store/selectors/userSelectors/isUserEssentialSelector';
import { useSelector } from 'react-redux';
import { createGQLDataContext } from 'utils/createDataContext';
import { formatPlural } from 'utils/formatPlural';
import { ChangeEventType } from '@bookjane2/bookjane-design-library/lib/common.types';
import { SSF_GLOBAL_CACHE_KEY } from 'services/ssfPersistenceService';
import { useTranslation } from 'hooks/useTranslation';
import { useGQLQuery } from 'hooks';
import {
  FetchCommunityBiddingPeriodQuery,
  FetchCommunityBiddingPeriodQueryVariables,
  FetchCommunityCustomersQuery,
  FetchCommunityCustomersQueryVariables,
} from 'types/graphql';
import { mapCommunityCustomers } from 'components/GroupedCustomersDropdown';
import {
  communityBiddingPeriodConfig,
  communityCustomersConfig,
} from 'pages/CreateShiftsPage/CreateShiftsPage.constants';
import { handleGenericErrorMessage } from 'utils/handleGenericErrorMessage';
import { getCurrentlySelectedLocation } from 'utils/getCurrentlySelectedLocation';
import { isShiftBiddingFeatureEnabledSelector } from 'store/selectors/featureFlagSelectors';
import { useResponsive } from 'providers/ResponsiveProvider/Responsive';

export const CreateShiftsPageContext = createGQLDataContext<{}>(createShiftsPageInitialState);

export const CreateShiftsPageProvider: FC = withMemo(({ children }): JSX.Element => {
  const { isMobile } = useResponsive();
  const [view, setView] = useState<CreateShiftsPageViewType>('CREATE_SHIFTS');
  const [isDiscardChangesDialogOpen, setIsDiscardChangesDialogOpen] = useState<boolean>(false);
  const [pendingConfirmationEvent, setPendingConfirmationEvent] = useState<ChangeEventType>(null);

  const { apiRequest } = useAPIActions();
  const location = getCurrentlySelectedLocation();
  const isUserEssential = useSelector(isUserEssentialSelector);
  const { t } = useTranslation();
  const isShiftBiddingFeatureEnabled = useSelector(isShiftBiddingFeatureEnabledSelector);
  const handleSubmit = useCallback(
    async ({
      groupedFormsValues,
      values,
    }: GroupedFormsBehaviorsOnSubmitArgsType): Promise<GroupedFormsOnSubmitResultType> => {
      await apiRequest(
        API_POST_createOrders(
          createOrdersPayloadTransformer({
            groupedFormsValues,
            values,
            isUserEssential,
            isShiftBiddingFeatureEnabled,
          }),
        ),
        {
          onSuccess: (res) => {
            const count = res?.data?.orders?.length;

            if (isMobile) {
              history.push('/dashboard/recent');
            } else {
              history.push('/dashboard/overview');
            }

            toastService.success(
              `${count} ${formatPlural(t('success:SHIFT'), count)} ${t('success:CREATED')}.`,
            );
          },
          onError: handleGenericErrorMessage,
        },
      );
      return {};
    },
    [apiRequest, isUserEssential, isShiftBiddingFeatureEnabled, isMobile, t],
  );

  const groupedFormsBehaviors = useGroupedFormsBehaviors({
    initialGroupedFormsState: [
      getInitialCreateShiftForm({
        isUserEssential,
      }),
    ],
    initialState: getInitialCreateShiftsPageForm({
      initialValues: {
        location: location,
        request_janes: [],
        estimatedTotalPayout: 0,
      },
    }),
    onSubmit: handleSubmit,
    isDirtyCheckEnabled: true,
    cacheConfig: {
      location: {
        cacheStorage: 'localStorage',
        cachekey: SSF_GLOBAL_CACHE_KEY(),
      },
    },
  });

  const { values, onChange, isSubmitDisabled, forceValidateForm, resetGroupedForms, groupedForms } =
    groupedFormsBehaviors;
  const { data: biddingPeriodData, fetch: fetchBiddingPeriod } = useGQLQuery<
    FetchCommunityBiddingPeriodQuery,
    FetchCommunityBiddingPeriodQuery,
    FetchCommunityBiddingPeriodQueryVariables,
    FetchCommunityBiddingPeriodQueryVariables
  >(communityBiddingPeriodConfig(parseInt(values.location.value)));

  const {
    data: customersData,
    fetch: fetchCustomers,
    called,
  } = useGQLQuery<
    FetchCommunityCustomersQuery,
    FetchCommunityCustomersQuery,
    FetchCommunityCustomersQueryVariables,
    FetchCommunityCustomersQueryVariables
  >(communityCustomersConfig(parseInt(values.location.value)));

  useEffect(() => {
    if (!biddingPeriodData) {
      fetchBiddingPeriod();
    }
  }, [fetchBiddingPeriod, biddingPeriodData]);

  useEffect(() => {
    if (!customersData && !called) {
      fetchCustomers();
    }
  }, [fetchCustomers, customersData, called]);

  const customers = useMemo(
    () => mapCommunityCustomers(customersData?.fetchCommunity ?? null),
    [customersData],
  );

  const hasCustomers = customers?.length > 0;

  const handleChange = (e: ChangeEventType) => {
    onChange(e);
    resetGroupedForms();
    setView('CREATE_SHIFTS');
    setPendingConfirmationEvent(null);
    setIsDiscardChangesDialogOpen(false);
  };

  const handleDiscardChangesDialogCancel = () => {
    setPendingConfirmationEvent(null);
    return setIsDiscardChangesDialogOpen(false);
  };

  const handleDiscardChangesDialogAction = () => {
    if (pendingConfirmationEvent) {
      onChange(pendingConfirmationEvent);
      resetGroupedForms();
      setView('CREATE_SHIFTS');
      setPendingConfirmationEvent(null);
      setIsDiscardChangesDialogOpen(false);
    }
  };

  const handleConfirmBooking: MouseEventHandler<HTMLButtonElement> = useCallback(
    async (e) => {
      e.preventDefault();

      if (hasCustomers && validateAndSetupCustomerValidation(groupedForms)) {
        forceValidateForm();
        return;
      }

      if (isSubmitDisabled) {
        forceValidateForm();
      } else {
        setView('SHIFTS_SUMMARY');
      }
    },
    [forceValidateForm, isSubmitDisabled, hasCustomers, groupedFormsBehaviors, t],
  );
  return (
    <CreateShiftsPageContext.Provider
      value={{
        groupedFormsBehaviors,
        view,
        biddingPeriods: biddingPeriodData,
        customersData: customers,
        hasCustomers,
        goToView: setView,
        values,
        onChange: handleChange,
        onConfirmBooking: handleConfirmBooking,
        fetch,
        isDiscardChangesDialogOpen,
        handleDiscardChangesDialogCancel,
        handleDiscardChangesDialogAction,
      }}
    >
      {children}
    </CreateShiftsPageContext.Provider>
  );
});

export const useCreateShiftsPageContext = (): ICreateShiftsPageContext =>
  useContext(CreateShiftsPageContext);
